import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Select } from "risa-oasis-ui_v2";
import CustomTable from "../../../../components/custom-table/custom-table";
import { ProviderOption } from "../../../../data-model/providerOptionModel";
import { OrganizationType } from "../../../../enums/organizationTypes";
import { ProviderOptionsTableColumnKeys } from "../../../../enums/tableColumnKeys";
import {
  clearProviderOptions,
  createProviderOption,
  deleteProviderOption,
  fetchProviderOptions,
  updateProviderOption,
} from "../../../../redux/slice/providerOptionsConfigSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import DeleteProviderOptionConfirm from "./components/DeleteProviderOptionConfirm";
import ProviderOptionFormModal from "./components/ProviderOptionFormModal";
import "./providerOptionsConfiguration.scss";
import { ProviderOptionsTableHeader } from "./providerOptionsTableData";

const organizationOptions = [
  { label: "NYCBS", value: OrganizationType.NYCBS_PHARMA },
  { label: "Astera", value: OrganizationType.ASTERA },
  { label: "UCBC", value: OrganizationType.UCBC },
  { label: "CHC", value: OrganizationType.CHC },
  { label: "MBM", value: OrganizationType.MBM },
  { label: "NYCBS HYD", value: OrganizationType.NYCBS_HYD },
];

const buildTableData = (options: ProviderOption[]) =>
  options.map((option, index) => ({
    [ProviderOptionsTableColumnKeys.NAME]: option.name,
    [ProviderOptionsTableColumnKeys.SHOW_ON_LIST]: {
      id: `provider-option-${index}-showOnList`,
      defaultChecked: option.show_on_list,
    },
    [ProviderOptionsTableColumnKeys.DELETE]: option.id,
    _originalData: option,
  }));

const ProviderOptionsConfiguration = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data, loading, error } = useSelector(
    (state: RootState) => state.providerOptionsConfig.providerOptions,
  );

  const [organization, setOrganization] = useState<string>("");
  const [isAddMode, setIsAddMode] = useState(false);
  const [editOption, setEditOption] = useState<ProviderOption | null>(null);
  const [deleteOption, setDeleteOption] = useState<ProviderOption | null>(null);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [workingTableData, setWorkingTableData] = useState<
    Record<string, any>[]
  >([]);

  useEffect(() => {
    if (organization) {
      dispatch(fetchProviderOptions(organization));
    } else {
      dispatch(clearProviderOptions());
    }
  }, [dispatch, organization]);

  useEffect(() => {
    if (!isDirty && data) {
      setWorkingTableData(buildTableData(data));
    }
  }, [data, isDirty]);

  const handleToggleChange = useCallback(
    (toggleData: { name: string; required: boolean; value: boolean }) => {
      const parts = toggleData.name.split("-");
      const rowIndex = parseInt(parts[2], 10);
      if (Number.isNaN(rowIndex)) return;

      setWorkingTableData((prev) => {
        const next = [...prev];
        const row = next[rowIndex];
        if (!row) return prev;
        next[rowIndex] = {
          ...row,
          [ProviderOptionsTableColumnKeys.SHOW_ON_LIST]: {
            ...row[ProviderOptionsTableColumnKeys.SHOW_ON_LIST],
            defaultChecked: toggleData.value,
          },
        };
        return next;
      });
      setIsDirty(true);
    },
    [],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatePromises = workingTableData
        .filter((row) => {
          const original = row._originalData as ProviderOption;
          const current =
            row[ProviderOptionsTableColumnKeys.SHOW_ON_LIST].defaultChecked;
          return original.show_on_list !== current;
        })
        .map((row) => {
          const original = row._originalData as ProviderOption;
          const current =
            row[ProviderOptionsTableColumnKeys.SHOW_ON_LIST].defaultChecked;
          return dispatch(
            updateProviderOption({
              facilityId: organization,
              docId: original.id,
              data: { show_on_list: current },
            }),
          ).unwrap();
        });

      await Promise.all(updatePromises);
      setIsDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (data) {
      setWorkingTableData(buildTableData(data));
    }
    setIsDirty(false);
  };

  const handleAdd = () => {
    setEditOption(null);
    setIsAddMode(true);
  };

  const handleRowClick = (rowData: any) => {
    const option = rowData._originalData as ProviderOption;
    if (option) {
      setIsAddMode(false);
      setEditOption(option);
    }
  };

  const handleDeleteIconClick = (optionId: string) => {
    const option = data?.find((o) => o.id === optionId);
    if (option) {
      setDeleteOption(option);
    }
  };

  const handleFormClose = () => {
    setIsAddMode(false);
    setEditOption(null);
    setSaving(false);
  };

  const handleFormSave = async (formData: Omit<ProviderOption, "id">) => {
    setSaving(true);
    try {
      if (isAddMode) {
        await dispatch(
          createProviderOption({ facilityId: organization, data: formData }),
        ).unwrap();
      } else if (editOption) {
        await dispatch(
          updateProviderOption({
            facilityId: organization,
            docId: editOption.id,
            data: formData,
          }),
        ).unwrap();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async (option: ProviderOption) => {
    await dispatch(
      deleteProviderOption({ facilityId: organization, docId: option.id }),
    ).unwrap();
  };

  const addButtonMeta = [
    {
      text: "Add Provider Option",
      onClick: handleAdd,
      disabled: false,
      buttonType: "primary" as const,
      size: "medium" as const,
    },
  ];

  return (
    <div className="provider-options-configuration flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex flex-shrink-0 flex-col border-b border-primaryGray-15 py-4">
          <div className="font-bold">Provider Options Configuration</div>
          <div className="text-small font-normal">
            Manage provider options per organization.
          </div>
        </div>

        <div className="mt-4 flex flex-shrink-0 items-end gap-4">
          <div className="min-w-[220px]">
            <Select
              id="organization"
              options={organizationOptions}
              label="Organization"
              placeholder="Select Organization"
              onOptionChange={(option: any) => setOrganization(option.value)}
              defaultValue={organization}
            />
          </div>
        </div>

        <div className="mb-1 mt-6 flex min-h-0 flex-1 flex-col overflow-y-auto">
          {!organization && (
            <div className="flex h-full items-center justify-center text-sm text-primaryGray-3">
              Please select an organization to view provider options.
            </div>
          )}

          {organization && error && (
            <div className="flex h-full items-center justify-center text-sm text-red-600">
              Failed to load provider options. Please try again.
            </div>
          )}

          {organization && !error && (
            <CustomTable
              tableHeaders={ProviderOptionsTableHeader}
              tableData={workingTableData}
              tableName="provider-options"
              itemsPerPage={10}
              pagesPerView={4}
              count={workingTableData.length}
              isFetching={loading}
              showOtherButton={true}
              otherButtonMeta={addButtonMeta}
              onDeleteIconClick={handleDeleteIconClick}
              onRowClick={handleRowClick}
              onChangeEmit={handleToggleChange}
              hideSearchBar={false}
            />
          )}
        </div>

        <div className="flex flex-shrink-0 flex-row justify-end gap-2 border-t border-primaryGray-15 py-4">
          <Button
            buttonType="secondary"
            size="medium"
            onClick={handleDiscard}
            disabled={!isDirty}
            children="Cancel"
          />
          <Button
            buttonType="primary"
            size="medium"
            onClick={handleSave}
            disabled={!isDirty || saving}
            children={saving ? "Saving..." : "Save"}
          />
        </div>
      </div>

      <ProviderOptionFormModal
        option={editOption}
        isAddMode={isAddMode}
        onSave={handleFormSave}
        onClose={handleFormClose}
        isSaving={saving}
      />
      <DeleteProviderOptionConfirm
        option={deleteOption}
        onDelete={handleDeleteConfirm}
        onClose={() => setDeleteOption(null)}
      />
    </div>
  );
};

export default ProviderOptionsConfiguration;
